import Promise from 'bluebird'
import _ from 'lodash'
import React from 'react'
import { browserHistory } from 'browser/history'

import { getDeferredUser } from 'shared-libs/helpers/utils'
import { Entity } from 'shared-libs/models/entity'

import apis from 'browser/app/models/apis'
import { Settings } from 'browser/app/models/settings'
import ComponentsMap from 'browser/components'
import { IBaseProps } from 'browser/components/atomic-elements/atoms/base-props'
import { EntityRenderer } from 'shared-libs/components/entity/renderer'
import { EntityFormValidationContext } from './entity-form-validation-context'
import {
  FormFocusTargetContext,
  IFormFocusTargetContext,
} from 'shared-libs/components/ui2/behavior/form-focus-target'

interface IEntityFormBlockProps extends IBaseProps {
  componentsMap?: Record<string, any>
  defaultValue?: object
  entity: Entity
  errors?: object
  onClose?: (entity: Entity) => void
  onSave: (entity: Entity) => Promise<any>
  onSelect?: (entity: Entity) => void
  settings: Settings
  state?: object
  uiContext?: object
  uiSchemaPath: string
}

interface IEntityFormBlockStates {
  entity: Entity
  errors: any
  hasSubmitted: boolean
  recipients: any[]
  title: string
  uiErrors: Record<string, string[]>
  uiErrorLabels: Record<string, string>
}

export class EntityFormBlock extends React.Component<IEntityFormBlockProps, IEntityFormBlockStates> {

  // Read the focus-target context served by the host provider so save
  // handlers below can call focusFirstError() without threading a ref
  // down from every host that mounts this block.
  public static contextType = FormFocusTargetContext as any
  declare context: IFormFocusTargetContext

  public static defaultProps: Partial<IEntityFormBlockProps> = {
    uiContext: {
      density: 'collapse',
      isHorizontalLayout: true,
    },
  }

  private store: any
  // Latest UI-based validation errors reported by the renderer. Held on the
  // instance so handleOnSave can read the freshest value synchronously without
  // waiting for a state flush.
  private currentUIErrors: Record<string, string[]> = {}

  constructor(props) {
    super(props)
    this.store = apis.getStore()
    const { entity } = props
    const title = `New ${entity.entityType}`
    this.state = {
      entity,
      errors: props.errors,
      hasSubmitted: false,
      recipients: [],
      title,
      uiErrors: {},
      uiErrorLabels: {},
    }
  }

  public UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.errors !== nextProps.errors) {
      this.setState({ errors: nextProps.errors })
    }
    if (this.props.entity !== nextProps.entity) {
      this.setState({ entity: nextProps.entity })
    }
  }

  public render() {
    const { defaultValue, settings, state, uiSchemaPath, uiContext } = this.props
    const { entity, errors, hasSubmitted, uiErrors, uiErrorLabels } = this.state
    // TODO(Peter): jank. I guess we need defaultValue here for the document-creation-flow
    const rendererState = {
      defaultValue,
      settings,
      ...this.state,
      ...state,
    }
    const renderContext = {
      ...uiContext,
      onClose: this.handleOnClose,
    }
    // Note that most entities need isHorizontalLayout, except load entity.
    // The validation context lets nested creation flows (DocumentCreationFlow)
    // render the error summary banner INSIDE the schema's ui:panel body,
    // under the panel title bar — impossible if the banner lived here since
    // this render is outside the ui:panel wrapper.
    return (
      <EntityFormValidationContext.Provider
        value={{ entity, errors, hasSubmitted, uiErrors, uiErrorLabels }}
      >
        <EntityRenderer
          applyDefaults={true}
          actions={this}
          componentsMap={this.props.componentsMap || ComponentsMap}
          context={renderContext}
          errors={errors}
          hasSubmitted={hasSubmitted}
          onChangeComplete={this.handleOnChange}
          onValidate={this.handleValidate}
          state={rendererState}
          uiSchemaPath={uiSchemaPath}
          value={entity}
        />
      </EntityFormValidationContext.Provider>
    )
  }

  private handleValidate = (
    uiErrors: Record<string, string[]>,
    uiErrorLabels: Record<string, string> = {},
  ) => {
    this.currentUIErrors = uiErrors
    if (
      !_.isEqual(this.state.uiErrors, uiErrors) ||
      !_.isEqual(this.state.uiErrorLabels, uiErrorLabels)
    ) {
      this.setState({ uiErrors, uiErrorLabels })
    }
  }

  /****************************************************************************/
  // UI Schema actions
  /****************************************************************************/

  private handleOnClose = () => {
    const { entity } = this.state
    this.props.onClose(entity)
  }

  private handleOnSelect = (entity) => {
    const { onSelect } = this.props
    onSelect(entity)
  }

  private handleOnChange = (entity) => {
    const { recipients } = this.state
    // TODO (David): Maybe this needs to be more generic since orders,
    // capacity, and anything can use the entity-form-block
    // This should be moved to the recipients field.
    const result = entity.resolveSubschemaByPath('uiSchema.web.entityCreationSection')
    const defaultRecipients = _.get(result, 'schema.metadata.defaultRecipients', [])
    this.setState({
      entity,
      recipients: _.union(defaultRecipients, recipients),
      title: `New ${entity.entityType}`,
    })
  }

  private handleOnDelete = () => {
    const { entity } = this.state
    entity.delete().then(() => {
      this.handleOnClose()
    })
  }

  /**
   * The submit gate. Public because there are two ways in and both must use
   * it: a schema wiring `ui:entityFooter` to the `handleOnSave` action (see
   * tldGlassTraining.json), and a host rendering its own footer, which calls
   * it through a ref. A host that calls its own `onSave` directly skips the
   * UI-required check below and saves an invalid form silently.
   */
  public handleOnSave = (): Promise<any> => {
    const { onSave } = this.props
    const { entity, recipients } = this.state

    // Pre-save gate: surface UI-driven required-field errors (requiredType:
    // 'required' set by schema authors on ui:form:* components) before hitting
    // schema validation or the network. Flipping hasSubmitted paints the
    // per-field error styling and reveals the summary banner.
    if (!_.isEmpty(this.currentUIErrors)) {
      this.setState({ hasSubmitted: true })
      void this.context?.focusFirstError?.()
      return Promise.resolve()
    }

    return Promise.resolve(entity.validate()).then((errors) => {
      if (!_.isEmpty(errors)) {
        this.setState({ errors, hasSubmitted: true })
        void this.context?.focusFirstError?.()
        return Promise.reject(errors)
      }

      const shares = recipients.map((recipient) => entity.addShare(getDeferredUser(recipient)))

      return Promise.all([onSave(entity), ...shares])
    })
  }

  private handleRecipientsChange = (recipients) => {
    this.setState({ recipients })
  }

  private handleOnSendInvite = () => {
    const { entity } = this.state
    entity.set('invite.status', 'Sent')
    void this.handleOnSave()
  }
}
